import { Button } from "@/components/ui/button";
import { InfiniteScrollText } from "@/components/ui/infinite-scroll-text";
import { ClassCard } from "@/components/classes/class-card";
import { toClassCardData } from "@/components/classes/class-card-data";
import type { ClassWithProfile } from "@/lib/api/classes";
import { fetchAllUpcomingClasses } from "@/lib/api/classes";
import { CardSkeleton, StudioCardSkeleton } from "@/components/ui/skeletons/card-skeleton";
import { StudioCard } from "@/components/studio-card";
import { supabase } from "@/lib/supabase";
import type { Studio } from "@/types/database";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export function HomePage() {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);

  const [classes, setClasses] = useState<ClassWithProfile[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedStudios();
    fetchUpcomingClasses();
  }, []);

  const fetchFeaturedStudios = async () => {
    try {
      const { data, error } = await supabase
        .from("studios")
        .select("*")
        .eq("is_live", true)
        .order("created_at", { ascending: true })
        .limit(3);

      if (error) throw error;
      setStudios(data || []);
    } catch (error) {
      console.error("Error fetching studios:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingClasses = async () => {
    try {
      const today = new Date().toISOString().split("T")[0];
      const data = await fetchAllUpcomingClasses({
        limit: 3,
        sort: "date_asc",
        from_date: today,
      });
      setClasses(data.classes);
    } catch (error) {
      console.error("Error fetching upcoming classes:", error);
    } finally {
      setClassesLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Top Half - Hero Section */}
      <div className="relative overflow-hidden">
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="space-y-6 md:space-y-8 max-w-4xl mx-auto py-16 md:py-24"
          >
            {/* Main heading with bigger sizing, extra bold Benzin */}
            <h1
              className="text-6xl md:text-7xl xl:text-8xl font-bold"
              style={{ lineHeight: '0.85' }}
            >
              find your
              <span className="block">
                perfect <img src="/smile.svg" alt="smile" className="inline-block w-[0.8em] h-[0.8em] pt-2 align-baseline" />
              </span>
              <span className="block">
                dance space.
              </span>
            </h1>

            {/* Subheading with better mobile readability */}
            <p className="text-md sm:text-lg md:text-xl">
              Manage your dance studio, host pop-up classes, and
              build your community — all in one platform.
            </p>

            {/* Action buttons - stack on mobile, side by side on sm+ */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-4 md:pt-8">
              <Link to="/classes">
                <Button
                  size="lg"
                  variant="outline-on-light"
                  className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold bg-card text-on-dark hover:bg-card/80"
                >
                  <span className="italic">BROWSE CLASSES →→→→</span>
                </Button>
              </Link>
              <Link to="/studios">
                <Button
                  size="lg"
                  variant="outline-on-light"
                  className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold"
                >
                  <span className="italic">BROWSE STUDIOS →→→→</span>
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Half - Off-Black Section */}
      <div className="bg-surface-primary py-12">
        {/* Instructors Marquee — above classes */}
        {(classesLoading || classes.length > 0) && (
          <div className="mb-12">
            <InfiniteScrollText
              text="BESTEST INSTRUCTORS EVER * "
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-benzin font-bold text-diamond leading-none"
              direction="right"
            />
          </div>
        )}

        {/* Upcoming Classes Section */}
        {(classesLoading || classes.length > 0) && (
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {classesLoading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <CardSkeleton key={i} showFooter={false} />
                  ))
                : classes.map((cls) => (
                    <ClassCard
                      key={cls.id}
                      data={toClassCardData(cls)}
                      showDate={true}
                      hideLocation={true}
                      hideStatusBadge={true}
                      hidePrice={true}
                    />
                  ))}
            </div>

            {!classesLoading && classes.length > 0 && (
              <div className="flex justify-center mt-8">
                <Link to="/classes">
                  <Button variant="outline" size="lg">
                    BROWSE ALL CLASSES
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Studios Marquee — above studios */}
        <div className="mb-12">
          <InfiniteScrollText
            text="BESTEST STUDIOS EVER * "
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-benzin font-bold text-diamond leading-none"
          />
        </div>

        {/* Featured Studios Section */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {Array.from({ length: 3 }).map((_, i) => (
                <StudioCardSkeleton key={i} />
              ))}
            </div>
          ) : studios.length === 0 ? (
            <div className="text-center py-8 text-on-dark-muted">
              No studios available yet. Check back soon!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
              {studios.map((studio) => (
                <StudioCard
                  key={studio.id}
                  studio={studio}
                  showProgressiveImage={true}
                  compact={true}
                />
              ))}
            </div>
          )}

          {!loading && studios.length > 0 && (
            <div className="flex justify-center mt-8">
              <Link to="/studios">
                <Button variant="outline" size="lg">
                  BROWSE ALL STUDIOS
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
